import RenderOnViewportEntry from '@creator/ui/components/RenderOnViewportEntry/RenderOnViewportEntry';
import { ReactLazyPreload } from '@creator/ui/utils/react';
import { cn } from '@creator/ui/utils/ui';
import { pushToDataLayer } from '@src/services/gtmService';
import storageService from '@src/services/storage-service';
import { getBasicPostEventData } from '@src/store/posts/helpers/analytics';
import { isPostPublished } from '@src/store/posts/helpers/posts';
import { usePost } from '@src/store/posts/hooks/usePost';
import { propsEqualityFn } from '@src/utils/object-utils/object-utils';
import { isCurrentPathFirstEverVisitPath } from '@src/utils/url-utils/url-utils';
import { type FC, memo, useEffect, useLayoutEffect, useRef } from 'react';
import ParentPostCard from '../ParentPostCard/ParentPostCard';
import PostCardHeader from '../PostCard/PostCardHeader';
import PostStatusIndicator from './PostStatusIndicator';
import PostViewDetails from './PostViewDetails';

// Lazy load CommentsSection and ChildPostsList
const CommentsSection = ReactLazyPreload(() => import('@src/pages/TokenPage/PostTab/CommentsSection'));
const ChildPostsList = ReactLazyPreload(() => import('@src/pages/TokenPage/PostTab/ChildPostsList'));
export interface PostViewProps {
    className?: string;
    postId: string;
    tokenName: string;
    highlightCommentId?: string;
    loadReplyId?: string;
    onExpand?: () => void;
}

const PostView: FC<PostViewProps> = props => {
    const { tokenName, postId, className, highlightCommentId, onExpand } = props;

    const post = usePost(postId);
    const parentPostId = post?.parentPostId || '';
    const isFirstSetReferredBy = useRef(true);

    useEffect(() => {
        if (!post) return;
        if (!isFirstSetReferredBy.current) return;

        setReferredBy();
        isFirstSetReferredBy.current = false;
    }, [post]);

    useLayoutEffect(() => {
        if (!postId) return;
        pushToDataLayer({
            event: 'article_view',
            ...getBasicPostEventData(postId),
        });

        CommentsSection.preload();
        ChildPostsList.preload();
    }, [postId]);

    function setReferredBy() {
        if (!post) return;

        const referredBy = storageService.localStorage.get('referredBy');
        if (referredBy) return;

        if (!isCurrentPathFirstEverVisitPath()) return;

        storageService.localStorage.set('referredBy', post.publisherId);
    }

    function renderPostStatusIndicator() {
        if (!post || isPostPublished(post)) return null;
        return <PostStatusIndicator postId={postId} tokenName={tokenName} />;
    }

    function renderChildPostsList() {
        const totalChildPosts = post?.totalChildPosts || 0;
        if (totalChildPosts === 0) return null;

        return (
            <RenderOnViewportEntry>
                <ChildPostsList tokenName={tokenName} parentPostId={postId} />
            </RenderOnViewportEntry>
        );
    }

    return (
        <div className={cn('space-y-2', className)}>
            {renderPostStatusIndicator()}
            <div className="px-4 py-3 space-y-3">
                <ParentPostCard parentPostId={parentPostId} tokenName={tokenName} />
                <PostCardHeader postId={postId} tokenName={tokenName} showPostTitle={post?.kind === 'community'} />
                <PostViewDetails postId={postId} tokenName={tokenName} onExpand={onExpand} />
            </div>
            {renderChildPostsList()}
            <RenderOnViewportEntry>
                <CommentsSection highlightCommentId={highlightCommentId} tokenName={tokenName} postId={postId} />
            </RenderOnViewportEntry>
        </div>
    );
};

export default memo(PostView, propsEqualityFn);
